import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Header from '../components/Header';
import useAntiCheat from '../hooks/useAntiCheat';
import {
  Clock, ChevronLeft, ChevronRight, Flag, CheckCircle2, Circle,
  AlertTriangle, Loader2, Trophy, X, ShieldCheck, Video, Maximize, Eye,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const subjectColor = (subj) => {
  const s = (subj || '').toLowerCase();
  if (s.startsWith('phys')) return 'bg-blue-100 text-blue-700';
  if (s.startsWith('chem')) return 'bg-emerald-100 text-emerald-700';
  if (s.startsWith('math')) return 'bg-purple-100 text-purple-700';
  if (s.startsWith('bio')) return 'bg-rose-100 text-rose-700';
  return 'bg-slate-100 text-slate-700';
};

const ActiveMockTest = () => {
  const { mockId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [mock, setMock] = useState(location.state?.mock || null);
  const [loading, setLoading] = useState(!location.state?.mock);
  const [answers, setAnswers] = useState({}); // {q_idx: "A"|...}
  const [currentIdx, setCurrentIdx] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const startTimeRef = useRef(null);

  // ===== Anti-cheat state =====
  const [testStarted, setTestStarted] = useState(false);
  const [cameraSkipped, setCameraSkipped] = useState(false);
  const [focusStrikes, setFocusStrikes] = useState(0); // tab switches + fullscreen exits
  const violationsRef = useRef({ tab_switches: 0, fullscreen_exits: 0, copy_attempts: 0 });
  const timingsRef = useRef({});           // {q_idx: seconds}
  const qEnterTimeRef = useRef(Date.now());
  const antiCheat = useAntiCheat({ maxWarnings: 3, submitting: submitting || !!result || !testStarted });

  // Bootstrap: if mock not passed via state (e.g. user refreshed), fetch by route
  useEffect(() => {
    if (mock) return;
    // We don't know if it's daily or weekly — try daily then weekly.
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/daily-mocks/today`);
        if (data.id === mockId) { setMock(data); }
        else {
          const { data: w } = await axios.get(`${API_URL}/api/daily-mocks/weekly`);
          if (w.id === mockId) setMock(w);
        }
      } catch (e) {
        toast.error('Could not load mock. Returning to mocks page.');
        navigate('/daily-mocks');
      } finally {
        setLoading(false);
      }
    })();
  }, [mock, mockId, navigate]);

  // Init timer once mock loaded (countdown starts when test actually starts)
  useEffect(() => {
    if (!mock) return;
    setSecondsLeft(mock.duration_minutes * 60);
  }, [mock]);

  // Track per-question time spent
  useEffect(() => {
    if (!testStarted) return;
    qEnterTimeRef.current = Date.now();
    return () => {
      const secs = Math.round((Date.now() - qEnterTimeRef.current) / 1000);
      timingsRef.current[currentIdx] = (timingsRef.current[currentIdx] || 0) + secs;
    };
  }, [currentIdx, testStarted]);

  const handleSubmit = useCallback(async (auto = false, reason = '') => {
    if (submitting || !mock) return;
    setSubmitting(true);
    // flush current question timing
    timingsRef.current[currentIdx] = (timingsRef.current[currentIdx] || 0) + Math.round((Date.now() - qEnterTimeRef.current) / 1000);
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    const payload = {
      answers: Object.entries(answers).map(([q_idx, selected]) => ({ q_idx: parseInt(q_idx, 10), selected })),
      time_taken_seconds: Math.max(1, Math.floor((Date.now() - (startTimeRef.current || Date.now())) / 1000)),
      violations: {
        ...violationsRef.current,
        face_warnings: antiCheat.warnings,
        device_detections: 0,
        camera_unavailable: cameraSkipped,
      },
      question_timings: Object.entries(timingsRef.current).map(([q_idx, seconds]) => ({ q_idx: parseInt(q_idx, 10), seconds })),
      auto_submitted_reason: reason,
    };
    try {
      const { data } = await axios.post(`${API_URL}/api/daily-mocks/${mock.id}/submit`, payload);
      setResult(data);
      if (auto) toast.warning(reason === 'time_up' ? 'Time up! Auto-submitted.' : 'Test auto-submitted due to rule violations.');
      else toast.success(`Submitted. Score: ${data.score} / ${data.total}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
      setShowConfirm(false);
    }
  }, [submitting, mock, answers, currentIdx, antiCheat.warnings, cameraSkipped]);

  // ===== Anti-cheat listeners (active only during the test) =====
  useEffect(() => {
    if (!testStarted || result) return;
    const onVisibility = () => {
      if (document.hidden) {
        violationsRef.current.tab_switches += 1;
        setFocusStrikes((s) => s + 1);
      }
    };
    const onFullscreenChange = () => {
      if (!document.fullscreenElement) {
        violationsRef.current.fullscreen_exits += 1;
        setFocusStrikes((s) => s + 1);
      }
    };
    const block = (e) => {
      e.preventDefault();
      violationsRef.current.copy_attempts += 1;
      toast.warning('Copy/paste is disabled during the test.');
    };
    const blockCtx = (e) => e.preventDefault();
    document.addEventListener('visibilitychange', onVisibility);
    document.addEventListener('fullscreenchange', onFullscreenChange);
    document.addEventListener('copy', block);
    document.addEventListener('paste', block);
    document.addEventListener('contextmenu', blockCtx);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      document.removeEventListener('copy', block);
      document.removeEventListener('paste', block);
      document.removeEventListener('contextmenu', blockCtx);
    };
  }, [testStarted, result]);

  // Focus strikes: warn then auto-submit at 3
  useEffect(() => {
    if (!testStarted || result || focusStrikes === 0) return;
    if (focusStrikes >= 3) {
      toast.error('3 focus violations — auto-submitting your test.');
      handleSubmit(true, 'focus_violations');
    } else {
      toast.warning(`⚠️ Warning ${focusStrikes}/3: stay in fullscreen and don't switch tabs. Test auto-submits at 3 strikes.`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusStrikes]);

  // Camera proctoring strikes: auto-submit when over limit
  useEffect(() => {
    if (!testStarted || result) return;
    if (antiCheat.isOverLimit) handleSubmit(true, 'proctoring_violations');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [antiCheat.isOverLimit]);

  const startTest = async () => {
    try {
      await document.documentElement.requestFullscreen();
    } catch (e) { /* fullscreen may be blocked; still allow test with tab tracking */ }
    startTimeRef.current = Date.now();
    qEnterTimeRef.current = Date.now();
    setTestStarted(true);
  };

  // Timer tick (only while the test is running)
  useEffect(() => {
    if (!mock || result || !testStarted) return;
    const t = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(t);
          handleSubmit(true, 'time_up');
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [mock, result, testStarted, handleSubmit]);

  if (loading || !mock) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-10 h-10 animate-spin text-[#0EA5E9]" />
        </div>
      </div>
    );
  }

  // ===== Proctoring gate (before test starts) =====
  if (!testStarted && !result) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="proctor-gate-page">
        <Header />
        <main className="max-w-2xl mx-auto px-6 py-10">
          <div className="quiz-card p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-3xl bg-gradient-to-br from-indigo-500 to-violet-600 shadow-lg mb-4">
              <ShieldCheck className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-black text-[#111827] mb-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              Proctored {mock.type === 'daily' ? 'Daily Mock' : 'Full Mock'} · {mock.track}
            </h1>
            <p className="text-sm text-[#6B7280] mb-6">
              {mock.question_count} questions · {mock.duration_minutes} min · +4 / −{mock.negative_per_wrong} marking
            </p>

            <div className="text-left bg-slate-50 rounded-2xl p-5 mb-6 space-y-3">
              {[
                { icon: Video, text: 'Camera stays ON — AI checks that only you are visible (no phones/books)' },
                { icon: Maximize, text: 'Fullscreen is enforced — exiting fullscreen or switching tabs counts as a strike' },
                { icon: Eye, text: '3 strikes = automatic submission. Copy/paste is disabled.' },
              ].map((rule, i) => (
                <div key={i} className="flex items-start gap-3">
                  <rule.icon className="w-5 h-5 text-indigo-600 shrink-0 mt-0.5" />
                  <p className="text-sm text-[#374151]">{rule.text}</p>
                </div>
              ))}
            </div>

            {/* Camera preview */}
            <div className="flex justify-center mb-5">
              <div className="relative">
                <video
                  ref={antiCheat.videoRef}
                  autoPlay
                  muted
                  playsInline
                  className="w-48 h-36 rounded-2xl object-cover bg-slate-900 border-2 border-slate-200"
                  data-testid="proctor-camera-preview"
                />
                <span className={`absolute bottom-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-bold ${antiCheat.cameraGranted ? 'bg-emerald-500 text-white' : 'bg-rose-500 text-white'}`}>
                  {antiCheat.cameraGranted ? '● Camera live' : 'Camera off'}
                </span>
              </div>
            </div>

            {antiCheat.cameraError && (
              <p className="text-sm text-rose-600 mb-4" data-testid="camera-error-msg">{antiCheat.cameraError}</p>
            )}

            {antiCheat.cameraGranted ? (
              <button
                onClick={startTest}
                data-testid="start-proctored-test-btn"
                className="w-full px-5 py-3.5 rounded-2xl font-bold text-base bg-[#0EA5E9] text-white shadow-[0_4px_0_rgb(2,132,199)] active:translate-y-1 active:shadow-none hover:bg-[#0284C7]"
              >
                Enter Fullscreen & Start Test
              </button>
            ) : (
              <>
                <button
                  onClick={antiCheat.requestCamera}
                  data-testid="enable-camera-btn"
                  className="w-full px-5 py-3.5 rounded-2xl font-bold text-base bg-indigo-600 text-white hover:bg-indigo-700"
                >
                  Allow Camera to Continue
                </button>
                {antiCheat.cameraError && (
                  <button
                    onClick={() => { setCameraSkipped(true); startTest(); }}
                    data-testid="continue-without-camera-btn"
                    className="w-full mt-3 px-5 py-3 rounded-2xl font-bold text-sm bg-amber-100 text-amber-800 hover:bg-amber-200 border border-amber-200"
                  >
                    No camera on this device? Continue without camera (attempt will be marked)
                  </button>
                )}
              </>
            )}
            <button
              onClick={() => navigate('/daily-mocks')}
              data-testid="proctor-gate-back-btn"
              className="w-full mt-3 px-5 py-3 rounded-2xl font-bold text-sm bg-slate-100 text-[#6B7280] hover:bg-slate-200"
            >
              Back to Mocks
            </button>
          </div>
        </main>
      </div>
    );
  }

  // ===== Result view =====
  if (result) {
    const accuracy = result.correct + result.wrong + result.unattempted > 0
      ? Math.round((result.correct / (result.correct + result.wrong + result.unattempted)) * 100)
      : 0;
    return (
      <div className="min-h-screen" data-testid="mock-result-page">
        <Header />
        <main className="max-w-4xl mx-auto px-6 py-10">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-3xl bg-gradient-to-br from-amber-400 to-orange-500 shadow-lg mb-4">
              <Trophy className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-black text-[#111827] mb-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              {result.score} <span className="text-[#9CA3AF] text-3xl">/ {result.total}</span>
            </h1>
            <p className="text-base text-[#6B7280]">
              {accuracy}% accuracy · {result.correct} correct · {result.wrong} wrong · {result.unattempted} skipped
            </p>
          </div>

          {/* Integrity flags */}
          {result.is_flagged && (
            <div className="mb-6 p-4 rounded-2xl bg-rose-50 border-2 border-rose-200 flex items-start gap-3" data-testid="integrity-flag-banner">
              <AlertTriangle className="w-5 h-5 text-rose-500 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-bold text-rose-700">Integrity flags on this attempt</p>
                <p className="text-xs text-rose-600">
                  {(result.integrity_flags || []).map(f => f.replaceAll('_', ' ')).join(' · ')}. Flagged attempts may be reviewed and excluded from leaderboards.
                </p>
              </div>
            </div>
          )}

          {/* Subject-wise breakdown */}
          <div className="quiz-card p-6 mb-6" data-testid="subject-breakdown">
            <h2 className="text-lg font-black text-[#111827] mb-4" style={{ fontFamily: 'Fredoka, sans-serif' }}>Subject-wise Performance</h2>
            <div className="space-y-3">
              {Object.entries(result.subject_scores || {}).map(([subject, s]) => {
                const pct = s.total > 0 ? Math.round((s.correct / s.total) * 100) : 0;
                return (
                  <div key={subject} data-testid={`subject-row-${subject.toLowerCase()}`}>
                    <div className="flex justify-between items-center mb-1.5">
                      <span className={`px-2 py-0.5 rounded-md text-xs font-bold ${subjectColor(subject)}`}>{subject}</span>
                      <span className="text-sm font-bold text-[#111827]">{s.correct}/{s.total} ({pct}%) · {s.marks} marks</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-cyan-500 to-blue-500" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Per-question review */}
          <div className="quiz-card p-6 mb-6">
            <h2 className="text-lg font-black text-[#111827] mb-4" style={{ fontFamily: 'Fredoka, sans-serif' }}>Answer Review</h2>
            <div className="grid grid-cols-6 sm:grid-cols-10 gap-2">
              {result.per_question.map((q) => (
                <div
                  key={q.q_idx}
                  data-testid={`q-result-${q.q_idx}`}
                  className={`aspect-square rounded-lg flex items-center justify-center text-xs font-black ${
                    q.is_correct === true ? 'bg-emerald-100 text-emerald-700' :
                    q.is_correct === false ? 'bg-rose-100 text-rose-700' :
                    'bg-slate-100 text-slate-400'
                  }`}
                  title={`Q${q.q_idx + 1} · Selected: ${q.selected || '—'} · Correct: ${q.correct_answer}`}
                >
                  {q.q_idx + 1}
                </div>
              ))}
            </div>
            <div className="flex gap-4 mt-4 text-xs text-[#6B7280]">
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-emerald-200"></span> Correct</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-rose-200"></span> Wrong</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-slate-200"></span> Skipped</span>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => navigate('/daily-mocks')}
              data-testid="back-to-mocks-btn"
              className="flex-1 px-5 py-3 rounded-2xl font-bold text-sm bg-[#0EA5E9] text-white shadow-[0_4px_0_rgb(2,132,199)] active:translate-y-1 active:shadow-none hover:bg-[#0284C7]"
            >
              Back to Mock Tests
            </button>
          </div>
        </main>
      </div>
    );
  }

  // ===== Test-taking view =====
  const q = mock.questions[currentIdx];
  const totalQ = mock.questions.length;
  const answeredCount = Object.keys(answers).length;
  const mm = Math.floor(secondsLeft / 60).toString().padStart(2, '0');
  const ss = (secondsLeft % 60).toString().padStart(2, '0');
  const timeWarning = secondsLeft < 300;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="active-mock-page">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {/* Header strip */}
        <div className="quiz-card p-4 mb-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-[#6B7280]">
              {mock.type === 'daily' ? "Today's Quick Mock" : 'Weekly Full Mock'} · {mock.track}
            </p>
            <p className="text-lg font-black text-[#111827]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              {answeredCount} / {totalQ} answered
            </p>
          </div>
          <div className={`flex items-center gap-2 px-4 py-2 rounded-2xl font-black text-lg ${timeWarning ? 'bg-rose-100 text-rose-700' : 'bg-indigo-100 text-indigo-700'}`} data-testid="mock-timer">
            <Clock className="w-5 h-5" />
            {mm}:{ss}
          </div>
        </div>

        {/* Floating proctoring widget */}
        {!cameraSkipped && (
        <div className="fixed bottom-4 right-4 z-40" data-testid="proctor-widget">
          <div className="relative">
            <video
              ref={antiCheat.videoRef}
              autoPlay
              muted
              playsInline
              className="w-32 h-24 rounded-xl object-cover bg-slate-900 border-2 border-white shadow-lg"
            />
            <span className={`absolute top-1.5 left-1.5 w-2.5 h-2.5 rounded-full ${antiCheat.faceStatus === 'ok' ? 'bg-emerald-400' : 'bg-rose-500 animate-pulse'}`} />
            {(focusStrikes > 0 || antiCheat.warnings > 0) && (
              <span className="absolute -top-2 -right-2 px-2 py-0.5 rounded-full bg-rose-600 text-white text-[10px] font-black shadow" data-testid="strike-counter">
                {Math.max(focusStrikes, antiCheat.warnings)}/3
              </span>
            )}
          </div>
        </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_240px] gap-5">
          {/* Question */}
          <div className="quiz-card p-6" data-testid="question-card">
            <div className="flex items-center justify-between mb-4">
              <span className={`px-2.5 py-1 rounded-md text-xs font-bold ${subjectColor(q.subject)}`}>{q.subject}{q.topic ? ` · ${q.topic}` : ''}</span>
              <span className="text-xs text-[#6B7280]">Question {currentIdx + 1} of {totalQ}</span>
            </div>
            <h2 className="text-base sm:text-lg text-[#111827] mb-6 whitespace-pre-line leading-relaxed">
              {q.question}
            </h2>
            <div className="space-y-2.5">
              {q.options.map((opt) => {
                const letter = (opt.match(/^([A-D])[\.\)]/) || [])[1] || opt.charAt(0).toUpperCase();
                const isSelected = answers[currentIdx] === letter;
                return (
                  <button
                    key={opt}
                    onClick={() => setAnswers({ ...answers, [currentIdx]: letter })}
                    data-testid={`option-${letter}`}
                    className={`w-full text-left px-4 py-3 rounded-2xl border-2 transition-all flex items-start gap-3 ${
                      isSelected
                        ? 'border-[#0EA5E9] bg-cyan-50'
                        : 'border-slate-200 hover:border-slate-300 bg-white'
                    }`}
                  >
                    {isSelected ? <CheckCircle2 className="w-5 h-5 text-[#0EA5E9] shrink-0 mt-0.5" /> : <Circle className="w-5 h-5 text-slate-300 shrink-0 mt-0.5" />}
                    <span className="text-sm text-[#111827]">{opt}</span>
                  </button>
                );
              })}
            </div>

            <div className="flex justify-between items-center mt-6 pt-5 border-t border-slate-100">
              <button
                onClick={() => setCurrentIdx(Math.max(0, currentIdx - 1))}
                disabled={currentIdx === 0}
                data-testid="prev-question-btn"
                className="px-4 py-2.5 rounded-xl font-bold text-sm bg-slate-100 text-[#6B7280] hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
              >
                <ChevronLeft className="w-4 h-4" /> Prev
              </button>
              {currentIdx === totalQ - 1 ? (
                <button
                  onClick={() => setShowConfirm(true)}
                  data-testid="submit-mock-btn"
                  className="px-5 py-2.5 rounded-xl font-bold text-sm bg-emerald-600 text-white hover:bg-emerald-700 flex items-center gap-1.5 shadow-md"
                >
                  <Flag className="w-4 h-4" /> Submit Mock
                </button>
              ) : (
                <button
                  onClick={() => setCurrentIdx(Math.min(totalQ - 1, currentIdx + 1))}
                  data-testid="next-question-btn"
                  className="px-4 py-2.5 rounded-xl font-bold text-sm bg-[#0EA5E9] text-white hover:bg-[#0284C7] flex items-center gap-1"
                >
                  Next <ChevronRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Palette */}
          <aside className="quiz-card p-5 h-fit" data-testid="question-palette">
            <h3 className="text-sm font-black text-[#111827] mb-3" style={{ fontFamily: 'Fredoka, sans-serif' }}>Palette</h3>
            <div className="grid grid-cols-5 gap-1.5">
              {mock.questions.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentIdx(idx)}
                  data-testid={`palette-q-${idx}`}
                  className={`aspect-square rounded-md text-xs font-black transition-all ${
                    idx === currentIdx ? 'ring-2 ring-[#0EA5E9] bg-cyan-50 text-[#0EA5E9]' :
                    answers[idx] ? 'bg-emerald-500 text-white' :
                    'bg-slate-100 text-slate-500 hover:bg-slate-200'
                  }`}
                >
                  {idx + 1}
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowConfirm(true)}
              data-testid="palette-submit-btn"
              className="w-full mt-4 px-3 py-2.5 rounded-xl font-bold text-sm bg-emerald-600 text-white hover:bg-emerald-700 flex items-center justify-center gap-1.5 shadow-md"
            >
              <Flag className="w-4 h-4" /> Submit Mock
            </button>
          </aside>
        </div>
      </main>

      {/* Confirm dialog */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="confirm-submit-modal">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl">
            <div className="flex items-start justify-between mb-3">
              <div className="w-12 h-12 rounded-2xl bg-amber-100 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-amber-600" />
              </div>
              <button onClick={() => setShowConfirm(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            <h3 className="text-lg font-black text-[#111827] mb-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>Submit your mock?</h3>
            <p className="text-sm text-[#6B7280] mb-5">
              You answered <span className="font-bold text-[#111827]">{answeredCount} of {totalQ}</span>. Unanswered questions count as 0 (no negative).
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirm(false)}
                data-testid="cancel-submit-btn"
                className="flex-1 px-4 py-2.5 rounded-xl font-bold text-sm bg-slate-100 text-[#6B7280] hover:bg-slate-200"
              >
                Keep solving
              </button>
              <button
                onClick={() => handleSubmit(false)}
                disabled={submitting}
                data-testid="confirm-submit-btn"
                className="flex-1 px-4 py-2.5 rounded-xl font-bold text-sm bg-emerald-600 text-white hover:bg-emerald-700 flex items-center justify-center gap-1.5"
              >
                {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting</> : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActiveMockTest;
